import { useState, useEffect } from 'react';
import { PanelAnnotation, FigurePanel, useFigureCompositionStore } from '@/store/figure-composition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface AnnotationPropertiesProps {
  panel: FigurePanel;
  annotation: PanelAnnotation;
  showAdvanced: boolean;
}

export function AnnotationProperties({ panel, annotation, showAdvanced }: AnnotationPropertiesProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const updateAnnotation = useFigureCompositionStore((state) => state.updateAnnotation);
  const removeAnnotation = useFigureCompositionStore((state) => state.removeAnnotation);
  const setSelectedAnnotationId = useFigureCompositionStore((state) => state.setSelectedAnnotationId);
  const [expandedSections, setExpandedSections] = useState<string[]>(['content', 'position', 'style']);

  if (!currentComposition) return null;

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isExpanded = (sectionId: string) => expandedSections.includes(sectionId);

  const handleDelete = () => {
    try {
      removeAnnotation(panel.id, annotation.id);
      setSelectedAnnotationId(null);
      
      // Explicit focus management: return focus to annotations list
      setTimeout(() => {
        const annotationsSection = document.querySelector('[data-annotations-section]') as HTMLElement;
        if (annotationsSection) {
          const addButton = annotationsSection.querySelector('button') as HTMLElement;
          if (addButton) {
            addButton.focus();
          } else {
            annotationsSection.focus();
          }
        }
      }, 0);
    } catch (error) {
      console.error('Failed to delete annotation:', error);
      // Could show a toast notification here in the future
    }
  };

  const getAnnotationTypeLabel = () => {
    switch (annotation.type) {
      case 'text':
        return 'Text Annotation';
      case 'arrow':
        return 'Arrow Annotation';
      case 'bracket':
        return 'Bracket Annotation';
      default:
        return 'Annotation';
    }
  };

  // Get style defaults from theme - calculate font size from fontScale
  const baseAnnotationSize = 10; // Base size for annotations
  const fontScale = currentComposition.theme.fontScale ?? (currentComposition.theme.fontSize ? currentComposition.theme.fontSize / 8 : 1.0);
  const defaultFontSize = Math.round(baseAnnotationSize * fontScale);
  const fontSize = annotation.style?.fontSizePt ?? defaultFontSize;
  const color = annotation.style?.color ?? '#000000';
  const fontWeight = annotation.style?.fontWeight ?? 'normal';
  const fontStyle = annotation.style?.fontStyle ?? 'normal';
  const opacity = annotation.style?.opacity ?? 1;
  const strokeWidth = annotation.style?.strokeWidthPx ?? 2;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b">
        <div>
          <div className="text-sm font-semibold">{getAnnotationTypeLabel()}</div>
          <div className="text-xs text-muted-foreground">Panel {panel.label}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => {
            setSelectedAnnotationId(null);
          }}
          title="Close annotation properties"
          aria-label="Close annotation properties"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      {(annotation.type === 'text' || annotation.type === 'bracket') && (
        <>
          <Collapsible open={isExpanded('content')} onOpenChange={() => toggleSection('content')}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
              <div className="flex flex-col items-start">
                <h4 className="text-sm font-semibold">Content</h4>
                <p className="text-[11px] text-muted-foreground mt-0.5">Edit the text shown in this annotation.</p>
              </div>
              {isExpanded('content') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Text</Label>
                <Input
                  type="text"
                  className="h-8 text-sm"
                  value={annotation.content || ''}
                  onChange={(e) =>
                    updateAnnotation(panel.id, annotation.id, { content: e.target.value })
                  }
                  placeholder="e.g. p<0.05"
                  aria-label="Annotation text content"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
          <Separator />
        </>
      )}

      {/* Position */}
      <Collapsible open={isExpanded('position')} onOpenChange={() => toggleSection('position')}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
          <div className="flex flex-col items-start">
            <h4 className="text-sm font-semibold">Position</h4>
            <p className="text-[11px] text-muted-foreground mt-0.5">Set positions in panel coordinates (0–1).</p>
          </div>
          {isExpanded('position') ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-3">
          <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">X</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                  value={annotation.x.toFixed(2)}
                  onChange={(e) => {
                    const x = parseFloat(e.target.value);
                    if (!isNaN(x) && x >= 0 && x <= 1) {
                      try {
                        updateAnnotation(panel.id, annotation.id, { x });
                      } catch (error) {
                        console.error('Failed to update annotation X position:', error);
                      }
                    }
                  }}
                  aria-label="X position (0-1, normalized coordinates)"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Y</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                  value={annotation.y.toFixed(2)}
                  onChange={(e) => {
                    const y = parseFloat(e.target.value);
                    if (!isNaN(y) && y >= 0 && y <= 1) {
                      try {
                        updateAnnotation(panel.id, annotation.id, { y });
                      } catch (error) {
                        console.error('Failed to update annotation Y position:', error);
                      }
                    }
                  }}
                  aria-label="Y position (0-1, normalized coordinates)"
                />
              </div>
          </div>
          {(annotation.type === 'arrow' || annotation.type === 'bracket') && (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">X end</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                  value={annotation.x2 !== undefined ? annotation.x2.toFixed(2) : '0.5'}
                  onChange={(e) => {
                    const x2 = parseFloat(e.target.value);
                    if (!isNaN(x2) && x2 >= 0 && x2 <= 1) {
                      updateAnnotation(panel.id, annotation.id, { x2 });
                    }
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Y end</Label>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  min="0"
                  max="1"
                  step="0.01"
                  value={annotation.y2 !== undefined ? annotation.y2.toFixed(2) : '0.5'}
                  onChange={(e) => {
                    const y2 = parseFloat(e.target.value);
                    if (!isNaN(y2) && y2 >= 0 && y2 <= 1) {
                      updateAnnotation(panel.id, annotation.id, { y2 });
                    }
                  }}
                />
              </div>
            </div>
          )}
          {annotation.type === 'text' && (
            <div className="space-y-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Horizontal anchor</Label>
                <div className="flex gap-1">
                  <Button
                    variant={annotation.xanchor === 'left' || !annotation.xanchor ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { xanchor: 'left' })}
                  >
                    Left
                  </Button>
                  <Button
                    variant={annotation.xanchor === 'center' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { xanchor: 'center' })}
                  >
                    Center
                  </Button>
                  <Button
                    variant={annotation.xanchor === 'right' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { xanchor: 'right' })}
                  >
                    Right
                  </Button>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Vertical anchor</Label>
                <div className="flex gap-1">
                  <Button
                    variant={annotation.yanchor === 'top' || !annotation.yanchor ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { yanchor: 'top' })}
                  >
                    Top
                  </Button>
                  <Button
                    variant={annotation.yanchor === 'middle' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { yanchor: 'middle' })}
                  >
                    Middle
                  </Button>
                  <Button
                    variant={annotation.yanchor === 'bottom' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={() => updateAnnotation(panel.id, annotation.id, { yanchor: 'bottom' })}
                  >
                    Bottom
                  </Button>
                </div>
              </div>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">0 is left/top, 1 is right/bottom of the panel</p>
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* Style */}
      <Collapsible open={isExpanded('style')} onOpenChange={() => toggleSection('style')}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
          <div className="flex flex-col items-start">
            <h4 className="text-sm font-semibold">Style</h4>
            <p className="text-[11px] text-muted-foreground mt-0.5">Adjust text, color, and line weight.</p>
          </div>
          {isExpanded('style') ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-3">
          {(annotation.type === 'text' || annotation.type === 'bracket') && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Font size (pt)</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[fontSize]}
                    min={6}
                    max={24}
                    step={1}
                    onValueChange={([value]) =>
                      updateAnnotation(panel.id, annotation.id, {
                        style: { ...annotation.style, fontSizePt: value },
                      })
                    }
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    className="w-16 h-8 text-sm"
                    min="6"
                    max="24"
                    value={fontSize}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 6 && value <= 24) {
                        updateAnnotation(panel.id, annotation.id, {
                          style: { ...annotation.style, fontSizePt: value },
                        });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="annotation-bold"
                    checked={fontWeight === 'bold'}
                    onChange={(e) =>
                      updateAnnotation(panel.id, annotation.id, {
                        style: {
                          ...annotation.style,
                          fontWeight: e.target.checked ? 'bold' : 'normal',
                        },
                      })
                    }
                    className="h-4 w-4"
                  />
                  <Label htmlFor="annotation-bold" className="text-xs cursor-pointer">
                    Bold
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="annotation-italic"
                    checked={fontStyle === 'italic'}
                    onChange={(e) =>
                      updateAnnotation(panel.id, annotation.id, {
                        style: {
                          ...annotation.style,
                          fontStyle: e.target.checked ? 'italic' : 'normal',
                        },
                      })
                    }
                    className="h-4 w-4"
                  />
                  <Label htmlFor="annotation-italic" className="text-xs cursor-pointer">
                    Italic
                  </Label>
                </div>
              </div>
            </>
          )}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Color</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      className="h-8 w-16 p-1"
                      value={color}
                      onChange={(e) =>
                        updateAnnotation(panel.id, annotation.id, {
                          style: { ...annotation.style, color: e.target.value },
                        })
                      }
                      aria-label="Annotation color picker"
                    />
                    <Input
                      type="text"
                      className="flex-1 h-8 text-sm"
                      value={color}
                      onChange={(e) =>
                        updateAnnotation(panel.id, annotation.id, {
                          style: { ...annotation.style, color: e.target.value },
                        })
                      }
                      placeholder="#000000"
                      aria-label="Annotation color hex code"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Set the color for this annotation (text, arrow, or bracket)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Opacity</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[opacity]}
                min={0}
                max={1}
                step={0.1}
                onValueChange={([value]) =>
                  updateAnnotation(panel.id, annotation.id, {
                    style: { ...annotation.style, opacity: value },
                  })
                }
                className="flex-1"
              />
              <Input
                type="number"
                className="w-16 h-8 text-sm"
                min="0"
                max="1"
                step="0.1"
                value={opacity.toFixed(1)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value) && value >= 0 && value <= 1) {
                    updateAnnotation(panel.id, annotation.id, {
                      style: { ...annotation.style, opacity: value },
                    });
                  }
                }}
              />
            </div>
          </div>
          {(annotation.type === 'arrow' || annotation.type === 'bracket') && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Line width (px)</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[strokeWidth]}
                  min={1}
                  max={8}
                  step={0.5}
                  onValueChange={([value]) =>
                    updateAnnotation(panel.id, annotation.id, {
                      style: { ...annotation.style, strokeWidthPx: value },
                    })
                  }
                  className="flex-1"
                />
                <Input
                  type="number"
                  className="w-16 h-8 text-sm"
                  min="1"
                  max="8"
                  step="0.5"
                  value={strokeWidth}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 8) {
                      updateAnnotation(panel.id, annotation.id, {
                        style: { ...annotation.style, strokeWidthPx: value },
                      });
                    }
                  }}
                />
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Delete */}
      <Separator />
      <div className="space-y-2">
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={handleDelete}
          aria-label="Delete annotation"
        >
          Delete annotation
        </Button>
      </div>

      {/* Advanced Section */}
      {showAdvanced && (
        <>
          <Separator />
          <Collapsible
            open={isExpanded('advanced')}
            onOpenChange={() => toggleSection('advanced')}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
              <h4 className="text-sm font-semibold text-muted-foreground">Advanced</h4>
              {isExpanded('advanced') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              <div className="text-xs text-muted-foreground">
                Advanced annotation options coming soon (rotation, z-index, etc.)
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
}

